import React, { useState, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { MDBRow, MDBBtn, MDBCol, MDBCheckbox, MDBIcon } from 'mdb-react-ui-kit'
import { useImageCrop } from './hooks/useImageCrop.hook'
import { useHeadshotUpload } from './hooks/useHeadshotUpload.hook'
import { useGuidelinesCheck } from './hooks/useGuidelinesCheck.hook'
import { useHeadshotRefinement } from './hooks/useHeadshotRefinement.hook'
import ImagePreview from './components/ImagePreview'
import CropInterface from './components/CropInterface'
import GuidelinesModal from './components/GuidelinesModal'
import { ENV_CONFIG, UI_MESSAGES } from './AgentHeadshotUploader.constants'
import AgentProfileStore from './../AgentProfileIntake/AgentProfile.store'
import './AgentHeadshotUploader.scss'

/**
 * AgentHeadshotUploader - Refactored component for managing agent headshots
 * Features:
 * 1. Preview existing user profile image
 * 2. Button to open modal with profile pic guidelines
 * 3. File selector for uploading new photo
 * 4. Loading spinner on upload
 * 5. Updated preview for newly uploaded photos
 * 6. ReactCrop implementation (enabled/disabled by environment setting)
 *
 * @param {Object} props - Component props
 * @param {Function} props.onValidityChange - Callback when validity changes (deprecated, uses store)
 * @param {string} props.layout - Layout orientation: 'portrait' (single column) or 'landscape' (two columns)
 * @param {boolean} props.isReadOnly - Whether the form is in readonly mode
 * @param {boolean} props.isPermitted - Whether the user has permission to edit (overrides readonly)
 */
const AgentHeadshotUploader = ({ onValidityChange, layout, isReadOnly, isPermitted }) => {
  // Environment config (mocked for now - can be replaced with actual env check)
  const enableCrop = ENV_CONFIG.DEFAULT_ENABLE_CROP

  // Ref for hidden file input
  const fileInputRef = useRef(null)

  const [refineOnUpload, setRefineOnUpload] = useState(false)

  // Custom hooks
  const {
    imageSrc,
    crop,
    isProcessing,
    isProcessed,
    error: cropError,
    handleFileSelect,
    handleImageLoaded,
    handleCropChange,
    getCroppedImageBlob,
    resetCrop,
  } = useImageCrop(enableCrop)

  const {
    isUploading,
    uploadSuccess,
    uploadError,
    currentHeadshot,
    uploadHeadshot,
    refreshHeadshot,
    clearUploadStatus,
    getIsValid,
  } = useHeadshotUpload()

  const {
    hasViewedGuidelines,
    showGuidelinesModal,
    isFirstTimeView,
    checkBeforeUpload,
    handleGuidelinesModalClose,
    toggleGuidelinesModal,
  } = useGuidelinesCheck()

  const {
    refinementStatus,
    isRefined,
    isProcessing: isRefining,
    canRefine,
    isRefineLoading,
    isRevertLoading,
    refineHeadshot,
    revertHeadshot,
    resetRefinementStatus,
  } = useHeadshotRefinement(refreshHeadshot)

  /**
   * Handles click on "Select New Headshot" button
   * Checks guidelines before allowing file selection
   */
  const handleSelectClick = useCallback(() => {
    // Check if user has viewed guidelines
    const canProceed = checkBeforeUpload()

    if (canProceed && fileInputRef.current) {
      // User has viewed guidelines, open file picker
      fileInputRef.current.click()
    }
    // If canProceed is false, modal will be shown by checkBeforeUpload
  }, [checkBeforeUpload])

  /**
   * Handles proceed button click in guidelines modal
   * Closes modal and opens file picker
   */
  const handleProceedFromModal = useCallback(async () => {
    // Close the modal (this will update user meta if first time)
    await handleGuidelinesModalClose()

    // Small delay to ensure modal closes smoothly before opening file picker
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    }, 100)
  }, [handleGuidelinesModalClose])

  /**
   * Handles file input change
   */
  const handleFileInputChange = useCallback(
    e => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFileSelect(files)
      }
    },
    [handleFileSelect]
  )

  /**
   * Handles the upload button click
   */
  const handleUpload = useCallback(async () => {
    try {
      const imageBlob = await getCroppedImageBlob()
      const success = await uploadHeadshot(imageBlob)

      if (success) {
        resetCrop()
        resetRefinementStatus()
        setRefineOnUpload(false)
        if (refineOnUpload) await refineHeadshot()
      }
    } catch (err) {
      console.error('Upload failed:', err)
    }
  }, [getCroppedImageBlob, uploadHeadshot, resetCrop, resetRefinementStatus, refineOnUpload, refineHeadshot])

  const handleRefine = useCallback(async () => {
    await refineHeadshot()
  }, [refineHeadshot])

  const handleRevert = useCallback(async () => {
    await revertHeadshot()
  }, [revertHeadshot])

  /**
   * Handles the cancel button click
   */
  const handleCancel = useCallback(() => {
    resetCrop()
    clearUploadStatus()
    setRefineOnUpload(false)
  }, [resetCrop, clearUploadStatus])

  /**
   * Clear status messages after a delay
   */
  useEffect(() => {
    if (uploadSuccess || uploadError) {
      const timer = setTimeout(() => {
        clearUploadStatus()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [uploadSuccess, uploadError, clearUploadStatus])

  /**
   * Set initial validity on mount
   */
  useEffect(() => {
    AgentProfileStore.setValidity('headshot', getIsValid())
  }, [getIsValid])

  // Determine if user can upload/edit based on readonly and permission
  // Show controls when: NOT readonly OR has permission
  const canEdit = !isReadOnly || isPermitted

  // Determine if upload button should be disabled
  // Always wait for image processing to complete (sizing/ratio conversion)
  const isUploadDisabled = !imageSrc || isUploading || isProcessing || !isProcessed

  // Determine if portrait layout
  const isPortrait = layout === 'portrait'

  // Column sizing based on layout
  const columnSize = isPortrait ? '12' : '6'

  // Guidance for choosing a photo; sits directly under the upload action it supports.
  const guidelinesLink = (
    <button type='button' className='headshot-guidelines-link' onClick={toggleGuidelinesModal} disabled={isUploading}>
      <MDBIcon fas icon='circle-info' aria-hidden='true' />
      Photo guidelines
    </button>
  )

  return (
    <MDBRow
      id='AgentHeadshotUploaderComponent'
      className={`agent-headshot-uploader headshot-group ${isPortrait ? 'portrait-layout' : 'landscape-layout'}`}
      role='group'
      aria-labelledby='headshotGroupLabel'
    >
      <MDBCol size='12'>
        <h6 id='headshotGroupLabel' className='headshot-group__label'>
          Your Headshot
        </h6>
      </MDBCol>

      {/* Preview or Crop Interface */}
      <MDBCol size='12' md={columnSize}>
        {!imageSrc ? (
          // Show current headshot when no file is selected
          <div className='current-headshot-preview'>
            <ImagePreview
              headshotUrl={currentHeadshot}
              badge={
                isRefined && (
                  <span className='headshot-ai-chip'>
                    <MDBIcon fas icon='wand-magic-sparkles' aria-hidden='true' />
                    AI Enhanced
                  </span>
                )
              }
            />
            {uploadSuccess && (
              <div className='alert alert-success mt-2' role='alert'>
                {UI_MESSAGES.UPLOAD_SUCCESS}
              </div>
            )}
          </div>
        ) : enableCrop ? (
          // Show crop interface when file is selected AND crop is enabled
          <div className='crop-section'>
            <div className='crop-instructions'>
              <h5 className='crop-title'>Crop Your Photo</h5>
              <p className='crop-description'>
                Drag the corners to adjust the crop area. The selected area will be your profile picture.
              </p>
            </div>
            <CropInterface
              imageSrc={imageSrc}
              crop={crop}
              onImageLoaded={handleImageLoaded}
              onCropChange={handleCropChange}
              isProcessing={isProcessing}
              isProcessed={isProcessed}
              enableCrop={enableCrop}
            />
          </div>
        ) : (
          // Show selected image preview when crop is disabled
          <div className='crop-section'>
            <div className='crop-instructions'>
              <h5 className='crop-title'>Previewing New Headshot</h5>
              <p className='crop-description'>
                This is how your profile picture will appear. Click "Complete Upload" when ready.
              </p>
            </div>
            <div className='current-headshot-preview'>
              <div className='image-preview'>
                <img
                  src={imageSrc}
                  alt='Selected headshot preview'
                  style={{ maxHeight: '250px' }}
                  className='img-fluid'
                />
              </div>
            </div>
          </div>
        )}
      </MDBCol>

      {/* Upload Form */}
      <MDBCol size='12' md={columnSize}>
        <form className='upload-form'>
          {/* AI Photo Touch-Up panel - Only shown when no file is selected and headshot exists */}
          {canEdit && !imageSrc && currentHeadshot && (canRefine || isRefining || isRefined) && (
            <section
              className={`ai-refine-panel${isRefining ? ' is-processing' : ''}${isRefined ? ' is-refined' : ''}${
                refinementStatus === 'failed' ? ' is-failed' : ''
              }`}
              aria-labelledby='aiRefinePanelTitle'
              aria-live='polite'
            >
              <div className='ai-refine-panel__header'>
                <MDBIcon fas icon='wand-magic-sparkles' className='ai-refine-panel__icon' aria-hidden='true' />
                <h6 id='aiRefinePanelTitle' className='ai-refine-panel__title'>
                  {isRefining ? 'Enhancing Your Photo…' : 'AI Photo Touch-Up'}
                </h6>
                {isRefined && (
                  <span className='ai-refine-panel__badge'>
                    <MDBIcon fas icon='check' aria-hidden='true' /> Applied
                  </span>
                )}
              </div>

              <p className='ai-refine-panel__text'>
                {isRefined
                  ? 'Prefer your original photo? You can switch back anytime and enhance it again later.'
                  : isRefining
                  ? "This usually takes under a minute. We'll notify you when your enhanced photo is ready."
                  : refinementStatus === 'failed'
                  ? "Your last enhancement didn't finish, and your photo wasn't changed. You can try again."
                  : 'Better lighting, color and sharpness. Your original is saved, so you can switch back anytime.'}
              </p>

              {isRefining && (
                <div className='ai-refine-panel__progress'>
                  <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                  Processing
                </div>
              )}

              {canRefine && (
                <MDBBtn
                  type='button'
                  sm='true'
                  className='ai-refine-panel__btn'
                  onClick={handleRefine}
                  disabled={isRefineLoading || isUploading}
                >
                  {isRefineLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm mr-2' role='status' aria-hidden='true' />
                      Submitting...
                    </>
                  ) : (
                    'Refine with AI'
                  )}
                </MDBBtn>
              )}

              {isRefined && (
                <MDBBtn
                  type='button'
                  sm='true'
                  outline
                  className='ai-refine-panel__btn ai-refine-panel__btn--revert'
                  onClick={handleRevert}
                  disabled={isRevertLoading || isUploading}
                >
                  {isRevertLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm mr-2' role='status' aria-hidden='true' />
                      Reverting...
                    </>
                  ) : (
                    'Revert to Original'
                  )}
                </MDBBtn>
              )}
            </section>
          )}

          {/* File Input - Only shown when no file is selected AND user can edit */}
          {canEdit && !imageSrc && (
            <div className='file-input-wrapper'>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              {/* Custom button that triggers file input */}
              <MDBBtn
                type='button'
                color='primary'
                className='btn-block headshot-select-btn'
                onClick={handleSelectClick}
                disabled={isUploading}
                style={{ marginBottom: '10px' }}
              >
                <MDBIcon fas icon='upload' className='mr-2' aria-hidden='true' />
                Upload a New Photo
              </MDBBtn>
              {guidelinesLink}
            </div>
          )}

          {/* Cancel Button - Only shown when file is selected AND user can edit */}
          {canEdit && imageSrc && (
            <MDBBtn
              type='button'
              sm='true'
              color='danger'
              className='btn-block'
              onClick={handleCancel}
              disabled={isUploading}
              style={{ marginBottom: '10px' }}
            >
              Cancel
            </MDBBtn>
          )}

          {/* Refine with AI Checkbox - Only shown when file is selected AND user can edit */}
          {canEdit && imageSrc && (
            <div className='refine-on-upload'>
              <MDBCheckbox
                id='headshotRefineOnUpload'
                label={
                  <>
                    <MDBIcon fas icon='wand-magic-sparkles' className='refine-on-upload__icon' aria-hidden='true' />
                    Refine with AI after upload
                  </>
                }
                checked={refineOnUpload}
                onChange={e => setRefineOnUpload(e.target.checked)}
                disabled={isUploading}
              />
            </div>
          )}

          {/* Upload Picture Button - Only shown when file is selected AND user can edit */}
          {canEdit && imageSrc && (
            <MDBBtn
              type='button'
              sm='true'
              color='success'
              className='btn-block'
              onClick={handleUpload}
              disabled={isUploadDisabled}
              style={{ marginBottom: '10px' }}
            >
              {isUploading ? (
                <>
                  <span className='spinner-border spinner-border-sm mr-2' role='status' aria-hidden='true' />
                  Uploading...
                </>
              ) : (
                'Complete Upload'
              )}
            </MDBBtn>
          )}
          {canEdit && imageSrc && guidelinesLink}

          {/* Error Messages */}
          {(cropError || uploadError) && (
            <div className='alert alert-danger mt-2' role='alert'>
              {cropError || uploadError}
            </div>
          )}

          {/* Read-only viewers still get the guidelines */}
          {!canEdit && guidelinesLink}
        </form>
      </MDBCol>

      {/* Guidelines Modal */}
      <GuidelinesModal
        isOpen={showGuidelinesModal}
        onToggle={toggleGuidelinesModal}
        onClose={handleGuidelinesModalClose}
        onProceed={isFirstTimeView ? handleProceedFromModal : null}
      />
    </MDBRow>
  )
}

AgentHeadshotUploader.propTypes = {
  onValidityChange: PropTypes.func,
  layout: PropTypes.oneOf(['portrait', 'landscape']),
  isReadOnly: PropTypes.bool,
  isPermitted: PropTypes.bool,
}

AgentHeadshotUploader.defaultProps = {
  onValidityChange: null,
  layout: 'landscape',
  isReadOnly: false,
  isPermitted: true,
}

export default observer(AgentHeadshotUploader)
